import {
  useState, useEffect, useRef,
  forwardRef, useImperativeHandle, useCallback,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { nanoid } from 'nanoid';
import miniQueryLoader from '@/helpers/miniQueryLoader';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import usePopoverFlip from '@/helpers/usePopoverFlip';

const fragmentGoogleItem = graphql`
  fragment googleLists_lists on Query @argumentDefinitions(
    pattern: {type: "String!"}
  ) {
    lists: getPlacesByPattern(pattern: $pattern) {
      gg_place_id: place_id
      slug
      title
    }
  }
`;
const queryGoogleLists = graphql`
  query googleListsQuery ($pattern: String!) {
    ...googleLists_lists @arguments(pattern: $pattern)
  }
`;

function GoogleLists({
  onClick, fwdRef, setPattern, anchorRef,
}) {
  const divRef = useRef();
  const [currentList, setCurrentList] = useState([]);
  const { comefrom } = useZusUserConnectedStore.getState();
  const isSearchPage = comefrom === 'search';
  // S'ouvre vers le haut quand le bas de la fenêtre couperait la liste.
  const flip = usePopoverFlip({ open: !!currentList?.length, panelRef: divRef, anchorRef });

  useImperativeHandle(fwdRef, () => ({
    setCurrentList,
  }), [currentList]);

  const handleClickOutside = useCallback((event) => {
    if (divRef && divRef.current && !divRef.current.contains(event.target)) {
      setPattern('');
    }
  });
  useEffect(() => {
    if (currentList?.length) {
      window.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [currentList]);

  const handleItemClick = (item) => {
    onClick(item);
    setCurrentList([]); // Vider la liste après la sélection
    setPattern(''); // Réinitialiser le pattern pour fermer la liste
  };

  if (!currentList?.length) return null;

  return (
    <div className={`absolute left-0 right-0 z-30 grid p-2 overflow-hidden text-left rounded-lg shadow-button bg-purple-6 ${flip ? 'bottom-full mb-2' : 'mt-2'}`} ref={divRef}>
      {currentList.map((gr) => (
        <div className={`transition rounded-lg cursor-pointer ${isSearchPage ? 'text-sm px-3 py-2 text-purple-4 hover:bg-purple-5 hover:text-purple' : 'px-4 py-3 text-purple-4 hover:bg-purple-5 hover:text-purple'}`} key={gr.id || nanoid()} onClick={() => handleItemClick(gr)}>{gr.title}</div>
      ))}
    </div>
  );
}

const MiniQueryLoaderGCLists = miniQueryLoader({
  Component: ({ data, setCurrentList }) => {
    const { lists } = useFragment(fragmentGoogleItem, data) || {};
    useEffect(() => {
      setCurrentList(lists);
    }, [data]);
    return null;
  },
  query: queryGoogleLists,
  providerVariables: ({ pattern }) => ({ pattern }),
});

function ListenerGC({ pattern: _pattern, fwdRef, setCurrentList }) {
  const [pattern, setPattern] = useState(_pattern);
  const [callQL, setCallQL] = useState(null);
  useImperativeHandle(fwdRef, () => ({
    setPattern: (value) => {
      if (value && typeof value === 'string') {
        setPattern(value);
        setCallQL(nanoid());
      } else if (value && ['function', 'object'].includes(typeof value) && value?.()) {
        setPattern(value?.());
        setCallQL(nanoid());
      } else {
        setCurrentList(null);
        setCallQL(null);
      }
    },
  }), [pattern]);

  return !callQL || <MiniQueryLoaderGCLists key={`fieldhubgooglelistener${pattern}`} pattern={pattern} setCurrentList={setCurrentList} />;
}
export default forwardRef(({ onClick, anchorRef }, ref) => {
  const refFragment = useRef();

  return (
    <>
      <ListenerGC
        fwdRef={ref}
        setCurrentList={(lists) => {
          refFragment?.current?.setCurrentList(lists);
        }}
      />
      <GoogleLists fwdRef={refFragment} anchorRef={anchorRef} setPattern={(value) => ref?.current?.setPattern(() => value)} onClick={onClick} />
    </>
  );
});
